import { ISearchTicketResult } from '@nx-ng/types';
import { orderBy } from 'lodash-es';
import {
  FareRequest,
  GetFareResponse,
  KnownPricePaymentMethod,
  Price,
  TicketProduct,
} from '../../../client/src';

export const TRAVEL_TIME_ANY_TIME_ID = '1';

export const mapFaresToTicketResults = (
  tickets: TicketProduct[]
): ISearchTicketResult[] => {
  return tickets
    .map(mapFareToTicketResult)
    .filter((ticket: ISearchTicketResult) => ticket.standardTicket.price > 0);
};

export const mapFareToTicketResult = (
  ticket: TicketProduct
): ISearchTicketResult => {
  const price = getPriceByPaymentMethod(
    KnownPricePaymentMethod.OneOff,
    ticket.price
  );

  const alias =
    (ticket.swiftTicketID ? ticket.swiftTicketID : ticket.mTicketID) || '';
  return {
    description: {
      customerType: ticket.passengerType?.description ?? '',
      travelMethod: ticket.ticketMode?.travelMethodName ?? '',
      zone: ticket.ticketArea?.travelArea ?? '',
      title: ticket.description ?? '',
    },
    standardTicket: {
      id: ticket.id!,
      title: ticket.description || '',
      alias,
      subtitle: `${alias}-subtitle`,
      price: price?.price || 0,
      dailyPrice: price?.pricePerDay || 0,
      paymentMethod: KnownPricePaymentMethod.OneOff,
      isDirectDebit: ticket.isDirectDebit || false,
      termDateFrom: ticket.termDates?.termDateFrom || '',
      termDateTo: ticket.termDates?.termDateTo || '',
    },
  };
};

export const selectSearchFares = ({
  valid,
  fares,
  values,
}: {
  valid: boolean;
  fares?: GetFareResponse | undefined;
  values?: Partial<FareRequest>;
}): ISearchTicketResult[] => {
  if (!valid) {
    return [];
  }
  const matchingFares = getFaresMatchingSelections({ fares, values });
  const faresToReturn = mapFaresToTicketResults(matchingFares);
  const orderedDirectDebitFares = orderBy(
    faresToReturn.filter(
      (fare: ISearchTicketResult) => fare.standardTicket.isDirectDebit
    ),
    (fare: ISearchTicketResult) => fare.standardTicket.price,
    'asc'
  );

  return [
    ...orderedDirectDebitFares,
    ...faresToReturn.filter(
      (fare: ISearchTicketResult) => !fare.standardTicket.isDirectDebit
    ),
  ];
};

export const getFaresMatchingSelections = ({
  fares,
  values,
}: {
  fares?: GetFareResponse | undefined;
  values?: Partial<FareRequest>;
}): TicketProduct[] => {
  if (!fares || !values) {
    return [];
  }

  return fares.filter((fare: TicketProduct) => {
    return (
      fare.passengerType?.typeCode === values.customerType &&
      fare.ticketArea?.id === values.zone &&
      fare.ticketMode?.id === values.travelMethod &&
      fare.ticketTime?.id === values.travelTime
    );
  });
};

export const getPriceByPaymentMethod = (
  paymentMethod: KnownPricePaymentMethod,
  prices: Price[] = []
): Price | undefined => {
  const findPrice = prices.find(
    (price: Price) => price.paymentMethod === paymentMethod
  );
  return findPrice;
};
